import type { PingResult, UserInfo } from '../types';

async function getPublicIP(): Promise<string> {
  const timeout = 5000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {  
    // First attempt: Try IPv4 specific endpoint
    const rIpv6 = await fetch('https://api64.ipify.org', {
      signal: controller.signal
    });
    
    if (!rIpv6.ok) {
      throw new Error(`HTTP error! status: ${rIpv6.status}`);
    }
    const ip6 = await rIpv6.text();
    
    
    const rIpv4 = await fetch('https://api.ipify.org', {
      signal: controller.signal
    });
    // Second attempt: Try general endpoint
    if (!rIpv4.ok) {
      throw new Error(`HTTP error! status: ${rIpv4.status}`);
    }
    
    const ip4 = await rIpv4.text();
    clearTimeout(timeoutId);
    return [ip4, ip6].join(" / ");
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out while fetching IP address');
      }
      throw new Error(`Failed to fetch IP address: ${error.message}`);
    }
    throw new Error('An unknown error occurred while fetching IP address');
  }
}

export async function pingEndpoint(url: string): Promise<PingResult> {
  const startTime = performance.now();
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(url, {
      method: 'HEAD', // Using HEAD instead of no-cors to get actual status
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    const endTime = performance.now();
    
    return {
      success: response.ok,
      latency: Math.round(endTime - startTime),
      statusCode: response.status || -1
    };
  } catch (error) {
    const endTime = performance.now();
    return {
      success: false,
      latency: Math.round(endTime - startTime),
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function getUserInfo(): Promise<UserInfo> {
  try {
    const ip = await getPublicIP();
    
    // For this version, we'll just return the IP address
    // since we're not using the geolocation service anymore
    return {
      ip,
      provider: 'ipify.org'
    };
  } catch (error) {
    console.error('Error fetching user info:', error);
    return {
      ip: 'Unable to detect',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}